import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lightbulb, Plus, X, Loader2, ArrowRight, Trash2, Search, Link2 } from "lucide-react";
import { API, authHeaders } from "@/constants/roles";

const COLUMNS = [
  { id: "inbox", label: "Inbox" },
  { id: "approved", label: "Approved" },
  { id: "script_ready", label: "Script ready" },
  { id: "in_production", label: "In production" },
  { id: "done", label: "Done" },
];
const PRIORITY_CLS = {
  urgent: "border-rose-500/30 bg-rose-500/15 text-rose-300",
  high: "border-amber-500/30 bg-amber-500/15 text-amber-300",
  normal: "border-[var(--pv-border)] text-[var(--pv-text-3)]",
  low: "border-[var(--pv-border)] text-[var(--pv-text-4)]",
};
const JOB_ROLES = ["Standard Animator", "High-Quality Animator", "Script Writer", "Video Editor"];
const inputCls =
  "w-full rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-bg)] px-4 py-2.5 text-sm font-medium text-[var(--pv-text)] placeholder:text-[var(--pv-text-4)] focus:border-cyan-400 focus:outline-none focus:ring-4 focus:ring-cyan-500/10 transition-all";

export const AdminIdeas = ({ canDelete = false }) => {
  const [ideas, setIdeas] = useState(null);
  const [q, setQ] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ title: "", hook: "", priority: "normal", tags: "" });
  const [sel, setSel] = useState(null);
  const [edit, setEdit] = useState(null);
  const [convert, setConvert] = useState({ role: "Standard Animator", pay: 55, video_duration: "", deadline_days: 7 });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/staff/ideas`, { headers: authHeaders() }).then((r) => setIdeas(r.data)).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/staff/ideas`, {
        title: addForm.title, hook: addForm.hook, priority: addForm.priority,
        tags: addForm.tags.split(",").map((t) => t.trim()).filter(Boolean),
      }, { headers: authHeaders() });
      toast.success("Idea added to the library.");
      setShowAdd(false);
      setAddForm({ title: "", hook: "", priority: "normal", tags: "" });
      load();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Could not add idea.");
    } finally { setBusy(false); }
  };

  const openDetail = (idea) => {
    setSel(idea);
    setEdit({ title: idea.title, hook: idea.hook || "", notes: idea.notes || "", script: idea.script || "", priority: idea.priority, status: idea.status, tags: (idea.tags || []).join(", ") });
    setConvert({ role: "Standard Animator", pay: 55, video_duration: "", deadline_days: 7 });
  };

  const save = async () => {
    setBusy(true);
    try {
      const r = await axios.patch(`${API}/staff/ideas/${sel.id}`, {
        title: edit.title, hook: edit.hook, notes: edit.notes, script: edit.script,
        priority: edit.priority, status: edit.status,
        tags: edit.tags.split(",").map((t) => t.trim()).filter(Boolean),
      }, { headers: authHeaders() });
      toast.success("Idea saved.");
      setSel(r.data);
      load();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Could not save idea.");
    } finally { setBusy(false); }
  };

  const doConvert = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/staff/ideas/${sel.id}/convert`, {
        eligible_roles: [convert.role], pay_by_role: { [convert.role]: Number(convert.pay) },
        video_duration: convert.video_duration, deadline_days: parseInt(convert.deadline_days) || 7,
      }, { headers: authHeaders() });
      toast.success(r.data.job_status === "draft" ? "Job draft created — the owner can publish it." : "Production job created and published.");
      setSel(null);
      load();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Could not convert idea.");
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm("Delete this idea permanently?")) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/staff/ideas/${sel.id}`, { headers: authHeaders() });
      toast.success("Idea deleted.");
      setSel(null);
      load();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Could not delete idea.");
    } finally { setBusy(false); }
  };

  const filtered = (ideas || []).filter((i) => !q || (i.title + (i.hook || "") + (i.tags || []).join(" ")).toLowerCase().includes(q.toLowerCase()));

  return (
    <div data-testid="ideas-board">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-[var(--pv-text)]">Idea Library</h1>
          <p className="mt-1 text-sm text-[var(--pv-text-4)]">Video ideas move from inbox to script to a real production job.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[var(--pv-text-4)]" />
            <input data-testid="ideas-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search ideas…" className={`${inputCls} pl-9`} />
          </div>
          <button data-testid="idea-add-btn" onClick={() => setShowAdd((v) => !v)} className="flex items-center gap-1.5 rounded-full bg-cyan-500 px-4 py-2.5 text-xs font-bold text-white hover:bg-cyan-600">
            <Plus className="h-3.5 w-3.5" /> Add idea
          </button>
        </div>
      </div>

      {showAdd && (
        <form data-testid="idea-add-form" onSubmit={add} className="mt-5 grid gap-3 rounded-3xl border border-cyan-500/20 bg-[var(--pv-card)] p-5 sm:grid-cols-2">
          <input data-testid="idea-title-input" required minLength={3} value={addForm.title} onChange={(e) => setAddForm((f) => ({ ...f, title: e.target.value }))} placeholder="Video title / concept" className={`${inputCls} sm:col-span-2`} />
          <input data-testid="idea-hook-input" value={addForm.hook} onChange={(e) => setAddForm((f) => ({ ...f, hook: e.target.value }))} placeholder="Hook — the first 3 seconds" className={inputCls} />
          <input data-testid="idea-tags-input" value={addForm.tags} onChange={(e) => setAddForm((f) => ({ ...f, tags: e.target.value }))} placeholder="Tags (comma separated)" className={inputCls} />
          <select data-testid="idea-priority-input" value={addForm.priority} onChange={(e) => setAddForm((f) => ({ ...f, priority: e.target.value }))} className={inputCls}>
            <option value="low">Low priority</option><option value="normal">Normal priority</option><option value="high">High priority</option><option value="urgent">Urgent</option>
          </select>
          <button data-testid="idea-save-new-btn" type="submit" disabled={busy} className="flex w-fit items-center gap-1.5 rounded-full bg-cyan-500 px-5 py-2.5 text-xs font-bold text-white hover:bg-cyan-600 disabled:opacity-50">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Add to library
          </button>
        </form>
      )}

      {!ideas ? (
        <p data-testid="ideas-loading" className="py-16 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-cyan-400" /></p>
      ) : (
        <div data-testid="ideas-columns" className="mt-6 flex gap-4 overflow-x-auto pb-4">
          {COLUMNS.map((col) => {
            const items = filtered.filter((i) => i.status === col.id);
            return (
              <div key={col.id} data-testid={`ideas-col-${col.id}`} className="w-64 shrink-0">
                <p className="flex items-center justify-between px-1 text-[11px] font-bold uppercase tracking-wider text-[var(--pv-text-4)]">
                  {col.label} <span className="rounded-full bg-[var(--pv-subtle)] px-2 py-0.5 text-[10px]">{items.length}</span>
                </p>
                <div className="mt-3 space-y-3">
                  {items.map((idea) => (
                    <button key={idea.id} data-testid={`idea-card-${idea.id}`} onClick={() => openDetail(idea)}
                      className="block w-full rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-card)] p-4 text-left transition-all hover:-translate-y-0.5 hover:border-cyan-500/30">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-bold leading-snug text-[var(--pv-text)]">{idea.title}</p>
                        <span className={`shrink-0 rounded-full border px-1.5 py-0.5 text-[9px] font-bold uppercase ${PRIORITY_CLS[idea.priority] || PRIORITY_CLS.normal}`}>{idea.priority}</span>
                      </div>
                      {idea.hook && <p className="mt-1.5 line-clamp-2 text-xs text-[var(--pv-text-4)]">{idea.hook}</p>}
                      <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
                        {(idea.tags || []).slice(0, 3).map((t) => (
                          <span key={t} className="rounded-full bg-[var(--pv-subtle)] px-2 py-0.5 text-[10px] font-semibold text-[var(--pv-text-3)]">{t}</span>
                        ))}
                        {idea.linked_job && (
                          <span className="flex items-center gap-1 rounded-full bg-cyan-500/10 border border-cyan-500/25 px-2 py-0.5 text-[10px] font-bold text-cyan-300">
                            <Link2 className="h-2.5 w-2.5" /> {idea.linked_job.status.replace(/_/g, " ")}
                          </span>
                        )}
                      </div>
                    </button>
                  ))}
                  {items.length === 0 && <p className="rounded-2xl border border-dashed border-[var(--pv-border)] px-4 py-6 text-center text-[11px] text-[var(--pv-text-4)]">No ideas</p>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {sel && edit && (
        <div data-testid="idea-detail" className="fixed inset-0 z-50 flex justify-end bg-black/60 backdrop-blur-sm" onClick={() => setSel(null)}>
          <div className="h-full w-full max-w-lg overflow-y-auto border-l border-[var(--pv-border)] bg-[var(--pv-bg)] p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <p className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-cyan-400"><Lightbulb className="h-4 w-4" /> Idea detail</p>
              <button data-testid="idea-close-btn" onClick={() => setSel(null)} className="rounded-full p-2 text-[var(--pv-text-4)] hover:bg-[var(--pv-subtle)]"><X className="h-4 w-4" /></button>
            </div>
            <div className="mt-5 space-y-3">
              <input data-testid="idea-edit-title" value={edit.title} onChange={(e) => setEdit((f) => ({ ...f, title: e.target.value }))} className={inputCls} />
              <input data-testid="idea-edit-hook" value={edit.hook} onChange={(e) => setEdit((f) => ({ ...f, hook: e.target.value }))} placeholder="Hook" className={inputCls} />
              <textarea data-testid="idea-edit-notes" value={edit.notes} onChange={(e) => setEdit((f) => ({ ...f, notes: e.target.value }))} placeholder="Notes / outline" rows={3} className={inputCls} />
              <textarea data-testid="idea-edit-script" value={edit.script} onChange={(e) => setEdit((f) => ({ ...f, script: e.target.value }))} placeholder="Script (optional — carried into the production job)" rows={6} className={inputCls} />
              <div className="grid grid-cols-2 gap-3">
                <select data-testid="idea-edit-status" value={edit.status} onChange={(e) => setEdit((f) => ({ ...f, status: e.target.value }))} className={inputCls}>
                  {COLUMNS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                  <option value="archived">Archived</option>
                </select>
                <select data-testid="idea-edit-priority" value={edit.priority} onChange={(e) => setEdit((f) => ({ ...f, priority: e.target.value }))} className={inputCls}>
                  <option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option><option value="urgent">Urgent</option>
                </select>
              </div>
              <input data-testid="idea-edit-tags" value={edit.tags} onChange={(e) => setEdit((f) => ({ ...f, tags: e.target.value }))} placeholder="Tags (comma separated)" className={inputCls} />
              <button data-testid="idea-save-btn" onClick={save} disabled={busy} className="flex items-center gap-1.5 rounded-full bg-cyan-500 px-5 py-2.5 text-xs font-bold text-white hover:bg-cyan-600 disabled:opacity-50">
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Save changes
              </button>
            </div>

            {sel.linked_job ? (
              <div data-testid="idea-linked-job" className="mt-6 rounded-2xl border border-cyan-500/25 bg-cyan-500/10 p-4 text-xs text-cyan-200">
                In production as <b>{sel.linked_job.title}</b> — status: {sel.linked_job.status.replace(/_/g, " ")}.
                {sel.status !== "done" && " It completes automatically when the job is approved."}
              </div>
            ) : (
              <div className="mt-6 rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-card)] p-5">
                <p className="text-[11px] font-bold uppercase tracking-wider text-[var(--pv-text-4)]">Convert to production job</p>
                <div className="mt-3 grid grid-cols-2 gap-3">
                  <select data-testid="idea-convert-role" value={convert.role} onChange={(e) => setConvert((f) => ({ ...f, role: e.target.value, pay: e.target.value === "High-Quality Animator" ? 110 : e.target.value === "Standard Animator" ? 55 : f.pay }))} className={inputCls}>
                    {JOB_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                  <input data-testid="idea-convert-pay" type="number" min="1" value={convert.pay} onChange={(e) => setConvert((f) => ({ ...f, pay: e.target.value }))} placeholder="Pay (USD)" className={inputCls} />
                  <input data-testid="idea-convert-duration" value={convert.video_duration} onChange={(e) => setConvert((f) => ({ ...f, video_duration: e.target.value }))} placeholder="Duration (e.g. 45s)" className={inputCls} />
                  <input data-testid="idea-convert-deadline-days" type="number" min="1" max="90" value={convert.deadline_days} onChange={(e) => setConvert((f) => ({ ...f, deadline_days: e.target.value }))} placeholder="Days after claim" title="Days allowed after claim" className={inputCls} />
                </div>
                <button data-testid="idea-convert-btn" onClick={doConvert} disabled={busy} className="mt-3 flex items-center gap-1.5 rounded-full bg-emerald-500 px-5 py-2.5 text-xs font-bold text-white hover:bg-emerald-600 disabled:opacity-50">
                  {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ArrowRight className="h-3.5 w-3.5" />} Convert to job
                </button>
              </div>
            )}

            {canDelete && (
              <button data-testid="idea-delete-btn" onClick={remove} disabled={busy} className="mt-6 flex items-center gap-1.5 rounded-full border border-rose-500/30 bg-rose-500/10 px-4 py-2 text-xs font-bold text-rose-300 hover:bg-rose-500/20 disabled:opacity-50">
                <Trash2 className="h-3.5 w-3.5" /> Delete idea
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
